import { Component, inject, output, computed, ViewChild, TemplateRef, signal, input, ChangeDetectionStrategy } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { DialogHeaderComponent, BaseDialogComponent, ConfirmDialogComponent } from '@cumlaude/shared-components-dialogs';
import { RestService } from '@cumlaude/shared-services';
import { RAIChatConversatie } from '@cumlaude/service-contract';
import { toSignal } from '@angular/core/rxjs-interop';
import { getAgeGroup } from '@cumlaude/shared-utils';
import { BehaviorSubject, firstValueFrom, switchMap } from 'rxjs';
import { ImmediateInputComponent } from '../../shared/components/immediate-input/immediate-input.component';
import { MeatballMenuComponent, MeatballMenuItemComponent } from '@cumlaude/shared-components-menu';
import { TooltipDirective } from '@cumlaude/shared-components-overlays';
import { formatDate } from '@angular/common';

@Component({
	selector: 'app-chat-history-dialog',
	templateUrl: './chat-history-dialog.component.html',
	styleUrls: ['./chat-history-dialog.component.scss'],
	imports: [
		BaseDialogComponent,
		DialogHeaderComponent,
		ConfirmDialogComponent,
		ImmediateInputComponent,
		MeatballMenuComponent,
		MeatballMenuItemComponent,
		TooltipDirective,
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatHistoryDialogComponent {
	protected readonly restService = inject(RestService);
	protected readonly dialogRef = inject<DialogRef<RAIChatConversatie>>(DialogRef);
	private readonly dialog = inject(Dialog);

	/** In inspect mode zijn de chats readonly (niet bewerken/verwijderen) */
	inspect = input(false);

	@ViewChild('confirmDeleteDialog')
	confirmDeleteDialog!: TemplateRef<RAIChatConversatie>;

	private readonly refreshChats$ = new BehaviorSubject<void>(undefined);
	chats = toSignal(
		this.refreshChats$.pipe(
			switchMap(() =>
				this.restService.getAIChatConversaties({
					verwijderd: false,
				})
			)
		),
		{ initialValue: [] }
	);

	groupedChats = computed(() => {
		const chats = this.chats();
		if (!chats.length) return [];

		const groups: Array<{ header: string; chats: RAIChatConversatie[] }> = [];
		let currentGroup: { header: string; chats: RAIChatConversatie[] } | null = null;

		for (const chat of chats) {
			const ageGroup = getAgeGroup(chat.lastModified);

			if (!currentGroup || currentGroup.header !== ageGroup) {
				currentGroup = { header: ageGroup, chats: [] };
				groups.push(currentGroup);
			}

			currentGroup.chats.push(chat);
		}

		return groups;
	});

	selectChat = output<RAIChatConversatie | null>();

	editingChat = signal<RAIChatConversatie | null>(null);

	onChatSelected(chat: RAIChatConversatie) {
		this.selectChat.emit(chat);
		this.dialogRef.close(chat);
	}

	getTooltip(chat: RAIChatConversatie) {
		return `${chat.onderwerp} (${formatDate(chat.lastModified, 'short', 'nl-NL')})`;
	}

	async updateChat(chat: RAIChatConversatie, naam: string | undefined) {
		if (!naam) return;

		chat.onderwerp = naam;
		await firstValueFrom(this.restService.putAIChatConversatie(chat));
		this.refreshChats$.next();
	}

	async deleteChat(chat: RAIChatConversatie) {
		const dialogRef = this.dialog.open(this.confirmDeleteDialog, { data: chat });
		if (!(await firstValueFrom(dialogRef.closed))) return;

		chat.verwijderd = true;
		await firstValueFrom(this.restService.putAIChatConversatie(chat));
		this.refreshChats$.next();
	}
}
